import { Link, useLocation } from '@truepill/tpos-react-router'
import AddressEntry from 'components/AddressEntry'
import { EditHeader, PV1Card, useEditHeaderActions } from 'components/Card'
import { usePrescribedData } from 'hooks/usePrescribedData'
import moment from 'moment'
import { goToViewPatient } from 'routes'
import type { Address, Patient, Prescription } from 'types'
import { capitalize, formatAddress, formatPhoneNumber } from 'utils'
import { isHuman } from 'utils/Patient'
import { PV1PatientCardEdit } from './PV1PatientCardEdit'
import { PV1PatientCardTitle } from './PV1PatientCardTitle'

type PV1PatientCardProps = {
  patient: Patient
  prescription: Prescription
}

export const PV1PatientCard = ({ prescription, patient }: PV1PatientCardProps): JSX.Element => {
  const { search } = useLocation()
  const editHeaderActions = useEditHeaderActions({})
  const { rxPatientData } = usePrescribedData({ prescription, patient })

  if (!patient) {
    return <></>
  }

  const isHumanPatient = isHuman(patient)

  const reviewPatientData = {
    name: `${patient.firstName} ${patient.lastName}`,
    dob: `${moment(patient.dob).format('MM/DD/YYYY')}`,
    gender: capitalize(patient.gender),
    species: patient.species,
    weight: patient.weight?.value ? `${patient.weight.value} ${patient.weight.unit ?? ''}` : undefined,
    guardian: patient.guardian,
    address: patient.address?.home,
    phone: formatPhoneNumber(patient.contacts?.phone),
  }

  const rows = [
    {
      label: 'Patient Name:',
      prescribed: { value: rxPatientData?.name },
      dispensed: {
        value: (
          <Link target="_blank" rel="noopener noreferrer" to={goToViewPatient({ patientId: patient?._id, search })}>
            {reviewPatientData.name}
          </Link>
        ),
      },
      testId: 'name',
    },
    {
      label: 'Date of Birth:',
      prescribed: { value: rxPatientData?.dob },
      dispensed: { value: reviewPatientData.dob },
      testId: 'dob',
    },
    {
      label: 'Sex:',
      prescribed: { value: rxPatientData?.gender },
      dispensed: { value: reviewPatientData.gender },
      testId: 'gender',
    },
    ...(!isHumanPatient
      ? [
          {
            label: 'Species:',
            prescribed: { value: rxPatientData?.species },
            dispensed: { value: reviewPatientData.species },
            testId: 'species',
          },
          {
            label: 'Weight:',
            prescribed: {
              value: rxPatientData?.weight,
            },
            dispensed: { value: reviewPatientData.weight },
            testId: 'weight',
          },
          {
            label: 'Guardian:',
            prescribed: { value: rxPatientData?.guardian },
            dispensed: { value: reviewPatientData.guardian },
            testId: 'guardian',
          },
        ]
      : []),
    {
      label: 'Address:',
      prescribed: {
        value: rxPatientData?.address,
      },
      dispensed: {
        value: formatAddress(reviewPatientData?.address as Address),
      },
      testId: 'address',
    },
    {
      label: 'Phone:',
      prescribed: { value: rxPatientData?.phone },
      dispensed: { value: reviewPatientData.phone },
      testId: 'phone',
    },
  ]

  return editHeaderActions.isEditing ? (
    <PV1PatientCardEdit
      editHeaderActions={{ ...editHeaderActions, title: <PV1PatientCardTitle isHumanPatient={isHumanPatient} /> }}
      reviewPatientData={reviewPatientData}
      rxPatientData={rxPatientData}
      isHumanPatient={isHumanPatient}
      onSubmit={() => {}}
    />
  ) : (
    <PV1Card
      title={<EditHeader {...editHeaderActions} title={<PV1PatientCardTitle isHumanPatient={isHumanPatient} />} />}
      rows={rows}
    />
  )
}
